import { useCallback, useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  GetPractitionerDocument,
  GetPractitionerQuery,
  GetPractitionerQueryVariables,
  useUpdateUserEmailMutation,
  useUpdateUserFirstNameMutation,
  useUpdateUserLastNameMutation,
} from "../generated/graphql"
import { useApi } from "../utils/API"
import { Query } from "../utils/constants"
import { useGraphQLClient } from "./useGraphQLClient"
import { useSession } from "./useSession"

export const useUser = () => {
  const { session } = useSession()
  const API = useApi()
  const queryClient = useQueryClient()
  const graphQLClient = useGraphQLClient()
  const onSuccess = () =>
    queryClient.invalidateQueries({ queryKey: [Query.GET_PRACTITIONER] })
  const { mutate: updateEmail } = useUpdateUserEmailMutation(graphQLClient, {
    onSuccess,
  })
  const { mutate: updateFirstName } = useUpdateUserFirstNameMutation(
    graphQLClient,
    {
      onSuccess,
    }
  )
  const { mutate: updateLastName } = useUpdateUserLastNameMutation(
    graphQLClient,
    {
      onSuccess,
    }
  )

  const [userData, setUserData] = useState<GetPractitionerQuery | undefined>(
    undefined
  )

  useEffect(() => {
    // This effect is used to refetch the user data when the session changes
    const fetchData = async () => {
      if (session?.userId) {
        try {
          const resp = await graphQLClient.request<
            GetPractitionerQuery,
            GetPractitionerQueryVariables
          >(GetPractitionerDocument, {
            userId: session?.userId ?? "",
          })
          setUserData(resp)
        } catch (error) {
          return
        }
      }
    }
    fetchData()
  }, [session?.userId])

  const user = useMemo(
    () => ({
      id: userData?.users_by_pk?.id,
      email: userData?.users_by_pk?.account?.email,
      firstName: userData?.users_by_pk?.first_name,
      lastName: userData?.users_by_pk?.last_name,
      phoneNumber: userData?.users_by_pk?.phone_number,
      isOrgAdmin: (userData?.users_by_pk?.organization_admins ?? []).length > 0,
      organizations: userData?.users_by_pk?.organization_members.map(
        (member) => ({
          id: member.organization.id,
          name: member.organization.name ?? "",
          location: member.organization.location ?? "",
        })
      ),
    }),
    [
      userData?.users_by_pk?.account?.email,
      userData?.users_by_pk?.first_name,
      userData?.users_by_pk?.id,
      userData?.users_by_pk?.last_name,
      userData?.users_by_pk?.organization_admins,
      userData?.users_by_pk?.organization_members,
      userData?.users_by_pk?.phone_number,
    ]
  )

  return {
    user,

    updatePassword: useCallback(
      async () => await API.requestPasswordReset(user?.email ?? ""),
      [API, user?.email]
    ),
    updateEmail: useCallback(
      async (email: string) => {
        await API.changeEmail(email)
        updateEmail({ userId: user?.id, email })
      },
      [API, updateEmail, user?.id]
    ),
    updateFirstName: useCallback(
      (firstName: string) => {
        updateFirstName({ userId: user?.id, firstName })
      },
      [updateFirstName, user?.id]
    ),
    updateLastName: useCallback(
      (lastName: string) => {
        updateLastName({ userId: user?.id, lastName })
      },
      [updateLastName, user?.id]
    ),
  }
}
